import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BUSINESS } from "@/constants/business";
import { useLang } from "@/context/LangContext";
import { STRINGS } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: "",
  email: "",
  contact: "",
  treatment: "",
  preferred_time: "",
  session_type: "incall",
  notes: "",
  gifted: false,
};

const inputCls =
  "w-full bg-white/5 border border-white/15 rounded-lg px-4 py-3 text-sm text-white placeholder:text-neutral-500 focus:outline-none focus:border-[#a4133c] transition-colors";

export default function BookingForm() {
  const { lang } = useLang();
  const t = STRINGS[lang];
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/bookings`, { ...form, treatment: form.treatment || t.book.options[0] });
      toast.success(t.book.toastOk);
      setForm(EMPTY);
    } catch {
      toast.error(t.book.toastErr);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="booking" data-testid="booking-section" className="py-24 px-6 bg-[#10070a] border-t border-white/10">
      <div className="max-w-4xl mx-auto text-center mb-12">
        <span className="mono-lbl text-xs text-[#a4133c] tracking-[0.2em] uppercase">{t.book.label}</span>
        <h2 className="serif-disp text-4xl md:text-5xl font-light mt-2 mb-4">{t.book.title}</h2>
        <p className="text-neutral-400 text-sm max-w-lg mx-auto">
          {t.book.sub}
        </p>
        <p data-testid="screening-line" className="mono-lbl text-[11px] uppercase tracking-widest text-[#c9933b]/90 mt-4">
          {t.book.screening}
        </p>
      </div>

      <div className="max-w-3xl mx-auto bg-black/60 border border-white/15 p-8 md:p-10 rounded-2xl shadow-2xl">
        <form data-testid="booking-form" className="space-y-6" onSubmit={submit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="booking-name" className="block text-xs uppercase tracking-widest text-neutral-400 mb-2 font-mono">{t.book.name}</label>
              <input
                id="booking-name"
                data-testid="booking-name-input"
                type="text"
                placeholder={t.book.namePh}
                className={inputCls}
                value={form.name}
                onChange={set("name")}
                required
              />
            </div>
            <div>
              <label htmlFor="booking-contact" className="block text-xs uppercase tracking-widest text-neutral-400 mb-2 font-mono">{t.book.contact}</label>
              <input
                id="booking-contact"
                data-testid="booking-contact-input"
                type="text"
                placeholder={t.book.contactPh}
                className={inputCls}
                value={form.contact}
                onChange={set("contact")}
                required
              />
            </div>
          </div>

          <div>
            <label htmlFor="booking-email" className="block text-xs uppercase tracking-widest text-neutral-400 mb-2 font-mono">{t.book.emailLabel}</label>
            <input
              id="booking-email"
              data-testid="booking-email-input"
              type="email"
              placeholder={t.book.emailPh}
              className={inputCls}
              value={form.email}
              onChange={set("email")}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="booking-treatment" className="block text-xs uppercase tracking-widest text-neutral-400 mb-2 font-mono">{t.book.treatment}</label>
              <select
                id="booking-treatment"
                data-testid="booking-treatment-select"
                className="w-full bg-[#180a10] border border-white/15 rounded-lg px-4 py-3 text-sm text-neutral-200 focus:outline-none focus:border-[#a4133c] transition-colors"
                value={form.treatment}
                onChange={set("treatment")}
              >
                {t.book.options.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="booking-time" className="block text-xs uppercase tracking-widest text-neutral-400 mb-2 font-mono">{t.book.datetime}</label>
              <input
                id="booking-time"
                data-testid="booking-datetime-input"
                type="datetime-local"
                className={`${inputCls} text-neutral-300`}
                value={form.preferred_time}
                onChange={set("preferred_time")}
              />
            </div>
          </div>

          <div>
            <span className="block text-xs uppercase tracking-widest text-neutral-400 mb-2 font-mono">{t.book.where}</span>
            <div className="flex flex-col sm:flex-row gap-3">
              {[
                { value: "incall", label: t.book.incall, testid: "booking-incall-radio" },
                { value: "outcall", label: t.book.outcall, testid: "booking-outcall-radio" },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  data-testid={opt.testid}
                  aria-pressed={form.session_type === opt.value}
                  onClick={() => setForm((f) => ({ ...f, session_type: opt.value }))}
                  className={`flex-1 px-4 py-3 rounded-lg border text-xs uppercase tracking-widest transition-all ${
                    form.session_type === opt.value
                      ? "border-[#a4133c] bg-[#800f2f]/25 text-white shadow-[0_0_18px_rgba(164,19,60,0.35)]"
                      : "border-white/15 bg-white/5 text-neutral-400 hover:border-white/40"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {form.session_type === "outcall" && (
              <p data-testid="outcall-fee-note" className="mt-2 text-[11px] text-neutral-500 font-mono">
                {t.book.outcallNote}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="booking-notes" className="block text-xs uppercase tracking-widest text-neutral-400 mb-2 font-mono">{t.book.notes}</label>
            <textarea
              id="booking-notes"
              data-testid="booking-notes-textarea"
              rows="3"
              placeholder={t.book.notesPh}
              className={inputCls}
              value={form.notes}
              onChange={set("notes")}
            />
          </div>

          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input
              data-testid="booking-gift-checkbox"
              type="checkbox"
              checked={form.gifted}
              onChange={(e) => setForm((f) => ({ ...f, gifted: e.target.checked }))}
              className="w-4 h-4 accent-[#c9933b]"
            />
            <span className="text-xs uppercase tracking-widest text-neutral-300 font-mono">{t.book.giftLabel}</span>
          </label>

          <button
            data-testid="booking-submit-btn"
            type="submit"
            disabled={sending}
            className="w-full py-4 bg-gradient-to-r from-[#800f2f] to-[#a4133c] hover:opacity-95 disabled:opacity-60 text-white text-xs uppercase tracking-[0.2em] font-semibold rounded-lg shadow-lg transition-all"
          >
            {sending ? t.book.sending : t.book.submit}
          </button>
        </form>

        <div className="mt-8 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-center gap-6 text-xs text-neutral-400">
          <span className="mono-lbl uppercase tracking-wider">{t.book.direct}</span>
          <a
            data-testid="whatsapp-link"
            href={BUSINESS.whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#25D366] hover:underline font-mono"
          >
            <span>●</span> {t.book.whatsapp}
          </a>
          <a
            data-testid="call-link"
            href={BUSINESS.phoneHref}
            className="inline-flex items-center gap-2 text-[#c9933b] hover:underline font-mono"
          >
            <span>●</span> {t.book.call} · {BUSINESS.phoneDisplay}
          </a>
          <a
            data-testid="email-link"
            href={BUSINESS.emailHref}
            className="inline-flex items-center gap-2 text-neutral-300 hover:underline font-mono"
          >
            <span>●</span> {BUSINESS.email}
          </a>
        </div>
      </div>
    </section>
  );
}
